import type { NodeCategory, WorkflowNode } from "../model";

export function kindColor(category: NodeCategory) {
  if (category === "source") return "source";
  if (category === "output") return "output";
  if (category === "logic") return "logic";
  if (category === "analytics") return "analytics";
  return "inference";
}

export type PortType =
  | "frame"
  | "audio"
  | "event"
  | "control"
  | "metadata"
  | "matrix";

export function portTypes(value: string): PortType[] {
  const normalized = value.toLowerCase();
  const types = new Set<PortType>();
  if (
    /frame|image|video|depth|mask|overlay|layer|camera|visual|people/.test(
      normalized,
    )
  )
    types.add("frame");
  if (/audio|pcm|spectrum/.test(normalized)) types.add("audio");
  if (/event|alert|motion|fall|occupancy|zone/.test(normalized))
    types.add("event");
  if (/control|command|error/.test(normalized)) types.add("control");
  if (/matrix|tensor|grid/.test(normalized)) types.add("matrix");
  if (!types.size && normalized !== "none") types.add("metadata");
  return [...types];
}

const framePorts = new Set([
  "frame",
  "overlay",
  "base",
  "layer",
  "main",
  "inset",
  "mask",
  "frame-a",
  "frame-b",
  "camera-a",
  "camera-b",
]);

const visualProcessorKinds = new Set([
  "background",
  "depth",
  "face",
  "object",
  "plate",
  "pose",
  "sam",
  "segment",
]);

const namedOutputTypes: Record<string, PortType[]> = {
  "motion-event": ["event"],
  "motion-mask": ["frame"],
  "foreground-mask": ["frame"],
  "annotated-frame": ["frame"],
  detections: ["metadata"],
  "mask-frame": ["frame"],
  segments: ["metadata"],
  faces: ["metadata"],
  plates: ["metadata"],
  poses: ["metadata"],
  tracks: ["metadata"],
  "track-event": ["event"],
  "above-event": ["event"],
  "below-event": ["event"],
  "inside-event": ["event"],
  "outside-event": ["event"],
  "pass-event": ["event"],
  "reject-event": ["event"],
  "matched-event": ["event"],
  "unmatched-event": ["event"],
  "handled-error": ["control"],
  "fallback-event": ["event"],
  "near-event": ["event"],
  "clear-event": ["event"],
};

export function nodePortTypes(
  node: WorkflowNode,
  side: "input" | "output",
  portID: string,
): PortType[] {
  if (node.kind === "mediaSplit" && side === "output")
    return portID === "audio" ? ["audio"] : ["frame"];
  if (node.kind === "split") return [];
  if (node.kind === "group")
    return portTypes(side === "input" ? node.input : node.output);
  if (side === "output" && node.kind === "depth") {
    if (portID === "depth-matrix") return ["matrix"];
    if (portID === "range") return ["metadata"];
    return ["frame"];
  }
  if (side === "output" && portID === "output" && node.outputPorts?.length)
    return portTypes(node.outputPorts[0].id);
  if (side === "output" && node.outputPorts?.some((port) => port.id === portID))
    return namedOutputTypes[portID] ?? portTypes(portID);
  if (side === "output" && visualProcessorKinds.has(node.kind)) return ["frame"];
  if (side === "input") {
    if (portID === "matrix") return ["matrix"];
    if (portID === "range" || portID === "metadata") return ["metadata"];
    if (framePorts.has(portID)) return ["frame"];
    if (portID === "metadata") return ["metadata"];
    if (/^event-/.test(portID)) return ["event"];
    if (/^detections-/.test(portID)) return ["metadata"];
    return portTypes(node.input);
  }
  return portTypes(node.output);
}

export function portType(value: string): PortType {
  const normalized = value.toLowerCase();
  if (normalized.includes("audio") || normalized.includes("pcm"))
    return "audio";
  if (
    normalized.includes("frame") ||
    normalized.includes("image") ||
    normalized.includes("video")
  )
    return "frame";
  if (normalized.includes("event") || normalized.includes("alert"))
    return "event";
  if (
    normalized.includes("control") ||
    normalized.includes("command") ||
    normalized.includes("error")
  )
    return "control";
  if (normalized.includes("matrix") || normalized.includes("tensor"))
    return "matrix";
  return "metadata";
}

export function portLabel(type: PortType) {
  return type === "frame"
    ? "FRAME"
    : type === "audio"
      ? "AUDIO"
      : type === "event"
        ? "EVENT"
        : type === "control"
          ? "CTRL"
          : "DATA";
}
